import { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ShieldCheck, CheckCircle2, XCircle, AlertTriangle, Loader2, QrCode } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { stringifyDetail } from '../lib/utils';

const API_BASE = window.location.origin;

const fmtDate = (iso) => {
    if (!iso) return '—';
    try {
        return new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
    } catch { return String(iso).slice(0, 10); }
};

export default function VoucherScan() {
    const { token } = useParams();
    const { t } = useTranslation();
    const [voucher, setVoucher] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [step, setStep] = useState('view'); // view | pin | done
    const [pin, setPin] = useState('');
    const [redeeming, setRedeeming] = useState(false);
    const [result, setResult] = useState(null);
    const [pinError, setPinError] = useState('');

    useEffect(() => {
        (async () => {
            try {
                const res = await fetch(`${API_BASE}/api/vouchers/scan/${token}`);
                const data = await res.json().catch(() => ({}));
                if (!res.ok) throw new Error(stringifyDetail(data.detail) || 'Voucher not found');
                setVoucher(data);
            } catch (e) {
                setError(e.message);
            } finally {
                setLoading(false);
            }
        })();
    }, [token]);

    const executeRedemption = async () => {
        if (pin.length !== 4) return;
        setRedeeming(true);
        setPinError('');
        try {
            const res = await fetch(`${API_BASE}/api/vouchers/redeem`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ code: voucher.code, pin }),
            });
            const data = await res.json().catch(() => ({}));
            if (res.ok) {
                setResult({ ok: true, data });
                setStep('done');
            } else if (res.status === 403) {
                setPinError(t('vouchers.invalidPin', 'Invalid PIN'));
            } else {
                setResult({ ok: false, message: stringifyDetail(data.detail) || t('vouchers.somethingWentWrong', 'Something went wrong') });
                setStep('done');
            }
        } catch {
            setPinError(t('vouchers.connectionErrorRetry', 'Connection error — try again'));
        } finally {
            setRedeeming(false);
        }
    };

    if (loading) {
        return (
            <div className="min-h-screen bg-slate-950 flex items-center justify-center">
                <div className="w-8 h-8 border-2 border-emerald-500 border-t-transparent rounded-full animate-spin" />
            </div>
        );
    }

    if (error) {
        return (
            <div className="min-h-screen bg-slate-950 flex items-center justify-center p-4" data-testid="scan-error">
                <div className="text-center space-y-3">
                    <AlertTriangle className="w-10 h-10 text-red-400 mx-auto" />
                    <p className="text-white font-medium">{error}</p>
                </div>
            </div>
        );
    }

    const invalidReasonText = {
        ALREADY_REDEEMED: t('vouchers.alreadyRedeemed', 'Already redeemed'),
        EXPIRED: t('vouchers.resultExpired', 'Expired'),
        VOIDED: t('vouchers.statusVoidedLabel', 'Voided'),
        NOT_YET_REDEEMABLE: t('vouchers.notYetRedeemable', 'Not yet redeemable'),
        // GIFT GUARD — a money prize card that still waits on the player's text.
        NOT_ACTIVATED: t('giftGuard.staffNotActivated'),
    }[voucher.invalid_reason] || voucher.invalid_reason;

    // ─── SUCCESS / FAILURE RESULT ───
    if (step === 'done') {
        return (
            <div className="min-h-screen bg-slate-950 flex items-center justify-center p-4" data-testid="scan-result-screen">
                <div className="w-full max-w-sm text-center space-y-5">
                    <div className={`w-16 h-16 rounded-full ${result.ok ? 'bg-emerald-600/20' : 'bg-red-600/20'} flex items-center justify-center mx-auto`}>
                        {result.ok
                            ? <CheckCircle2 className="w-8 h-8 text-emerald-400" />
                            : <XCircle className="w-8 h-8 text-red-400" />}
                    </div>
                    <h2 className="text-xl font-bold text-white">
                        {result.ok ? t('vouchers.resultRedeemed', 'Redeemed') : t('vouchers.resultError', 'Error')}
                    </h2>
                    {result.ok ? (
                        <div className="bg-slate-900 rounded-xl p-4 space-y-2 text-sm" data-testid="scan-redeem-success">
                            <div className="flex justify-between text-slate-400"><span>{t('vouchers.code', 'Code')}</span><span className="text-white font-mono">{result.data.code}</span></div>
                            <div className="flex justify-between text-slate-400"><span>{t('vouchers.value', 'Value')}</span><span className="text-emerald-400 font-semibold">${result.data.value}</span></div>
                            <div className="flex justify-between text-slate-400"><span>{t('vouchers.redeemedBy', 'Redeemed by')}</span><span className="text-white">{result.data.redeemed_by}</span></div>
                        </div>
                    ) : (
                        <p className="text-sm text-slate-400">{result.message}</p>
                    )}
                </div>
            </div>
        );
    }

    // ─── PIN ENTRY (Execute) ───
    if (step === 'pin') {
        return (
            <div className="min-h-screen bg-slate-950 flex items-center justify-center p-4" data-testid="scan-pin-screen">
                <div className="w-full max-w-xs space-y-6 text-center">
                    <div className="space-y-2">
                        <ShieldCheck className="w-10 h-10 text-emerald-500 mx-auto" />
                        <h1 className="text-lg font-semibold text-white">{t('vouchers.staffPin', 'Staff PIN')}</h1>
                        <p className="text-sm text-slate-400">{t('vouchers.enterPinToConfirm', 'Enter your 4-digit PIN to confirm')}</p>
                        <p className="text-xs text-slate-500 font-mono">{voucher.code} · ${voucher.value}</p>
                    </div>
                    <Input
                        data-testid="scan-pin-input"
                        type="password"
                        inputMode="numeric"
                        maxLength={4}
                        value={pin}
                        onChange={(e) => setPin(e.target.value.replace(/\D/g, '').slice(0, 4))}
                        onKeyDown={(e) => e.key === 'Enter' && pin.length === 4 && executeRedemption()}
                        placeholder="____"
                        className="text-center text-2xl tracking-[0.5em] bg-slate-900 border-slate-700 text-white h-14"
                        autoFocus
                    />
                    {pinError && <p className="text-sm text-red-400" data-testid="scan-pin-error">{pinError}</p>}
                    <Button
                        data-testid="scan-execute-confirm-btn"
                        onClick={executeRedemption}
                        disabled={pin.length !== 4 || redeeming}
                        className="w-full h-12 bg-emerald-600 hover:bg-emerald-700 font-semibold"
                    >
                        {redeeming ? <Loader2 className="w-4 h-4 animate-spin" /> : t('vouchers.executeGiftCard', 'EXECUTE GIFT CARD')}
                    </Button>
                    <button onClick={() => { setStep('view'); setPin(''); setPinError(''); }}
                        className="text-xs text-slate-500 hover:text-slate-400">
                        {t('common.back', 'Back')}
                    </button>
                </div>
            </div>
        );
    }

    // ─── VOUCHER VIEW (scan result — read-only) ───
    return (
        <div className="min-h-screen bg-slate-950 flex items-center justify-center p-4" data-testid="scan-voucher-screen">
            <div className="w-full max-w-sm space-y-4">
                <div className="flex items-center justify-center gap-2 text-slate-500 text-xs uppercase tracking-[0.25em]">
                    <QrCode className="w-4 h-4" /> {t('vouchers.qrScanTitle', 'Voucher Verification')}
                </div>

                <div className={`rounded-2xl overflow-hidden border ${voucher.valid ? 'border-emerald-600/40' : 'border-red-600/40'} bg-gradient-to-b from-slate-900 to-slate-800`}>
                    <div className={`h-2 ${voucher.valid ? 'bg-emerald-500' : 'bg-red-500'}`} />
                    <div className="p-6 space-y-4">
                        <div className="text-center space-y-2">
                            {voucher.logo_url && (
                                <img src={voucher.logo_url} alt="" className="h-12 mx-auto object-contain" data-testid="scan-logo" />
                            )}
                            <p className="text-slate-400 text-sm font-medium tracking-wide">{voucher.organization_name}</p>
                        </div>

                        <div className="text-center">
                            <p className={`text-5xl font-bold ${voucher.valid ? 'text-emerald-400' : 'text-slate-500'}`} data-testid="scan-value">
                                ${voucher.value}
                            </p>
                            <p className="text-slate-500 text-sm mt-1 font-mono" data-testid="scan-code">{voucher.code}</p>
                        </div>

                        {/* Validity state */}
                        {voucher.valid ? (
                            <div className="flex items-center justify-center gap-2 bg-emerald-600/15 border border-emerald-600/40 rounded-xl py-3" data-testid="scan-valid-badge">
                                <CheckCircle2 className="w-5 h-5 text-emerald-400" />
                                <span className="text-emerald-300 font-semibold">{t('vouchers.validVoucher', 'VALID')}</span>
                            </div>
                        ) : (
                            <div className="flex items-center justify-center gap-2 bg-red-600/15 border border-red-600/40 rounded-xl py-3" data-testid="scan-invalid-badge">
                                <XCircle className="w-5 h-5 text-red-400" />
                                <span className="text-red-300 font-semibold">{invalidReasonText}</span>
                            </div>
                        )}

                        <div className="bg-slate-800/50 rounded-xl p-4 space-y-2 text-sm">
                            <div className="flex justify-between text-slate-400">
                                <span>{t('vouchers.redeemableFrom', 'Redeemable from')}</span>
                                <span className="text-white">{fmtDate(voucher.redeemable_from || voucher.issued_at)}</span>
                            </div>
                            <div className="flex justify-between text-slate-400">
                                <span>{t('vouchers.expires', 'Expires')}</span>
                                <span className="text-white">{fmtDate(voucher.expires_at)}</span>
                            </div>
                            <div className="flex justify-between text-slate-400">
                                <span>{t('vouchers.validAt', 'Valid at')}</span>
                                <span className="text-white">{voucher.location_name || voucher.organization_name}</span>
                            </div>
                            {voucher.redeemed_at && (
                                <div className="flex justify-between text-slate-400">
                                    <span>{t('vouchers.redeemedAt', 'Redeemed at')}</span>
                                    <span className="text-white">{fmtDate(voucher.redeemed_at)}</span>
                                </div>
                            )}
                        </div>
                    </div>
                </div>

                {/* Execute only when VALID — scan itself never changes state */}
                {voucher.valid && (
                    <Button
                        data-testid="scan-execute-btn"
                        onClick={() => setStep('pin')}
                        className="w-full h-14 bg-emerald-600 hover:bg-emerald-700 text-base font-bold tracking-wide"
                    >
                        {t('vouchers.executeGiftCard', 'EXECUTE GIFT CARD')}
                    </Button>
                )}
                <p className="text-center text-xs text-slate-600">
                    {t('vouchers.scanStaffNote', 'Staff only — redemption requires your 4-digit PIN.')}
                </p>
            </div>
        </div>
    );
}
